import { Injectable } from "@angular/core";
import { act, Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/app.state";
import { WarehousesService } from "../services/warehouses.services";
import { catchError, finalize, map, of, switchMap, tap } from "rxjs";
import * as warehousesActions from './warehouses.actions'
import { APIResponse } from "src/app/core/models/api-response";
import { IWarehouse } from "../models/warehouses.models";
import { TranslateService } from "@ngx-translate/core";
import { CommonService } from "src/app/shared/common.service";

@Injectable()
export class WarehousesEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private warehousesService: WarehousesService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) { }

  FetchAllWarehousesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(warehousesActions.FetchAllWarehouses),
      tap(() => {
        this.store.dispatch(warehousesActions.SetWarehousesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.warehousesService.listAllWarehouses(action.pagination).pipe( 
          map((res: APIResponse<IWarehouse>) => warehousesActions.FetchAllWarehousesSuccess({ warehouses: res })),
          catchError(() => of(
            warehousesActions.FetchAllWarehousesError()
          )),
          finalize(() => {
          this.store.dispatch(warehousesActions.SetWarehousesLoader({ isLoading: false }));
        })
      )
      )
    )
  );
  
  LoadMoreWarehousesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(warehousesActions.LoadMoreWarehouses),
      tap(() => {
        this.store.dispatch(warehousesActions.SetWarehousesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.warehousesService.listAllWarehouses(action.pagination).pipe(
          map((res: APIResponse<IWarehouse>) => warehousesActions.LoadMoreWarehousesSuccess({ warehouses: res })),
          catchError(() => of(
            warehousesActions.LoadMoreWarehousesError()
          )),
          finalize(() => {
            this.store.dispatch(warehousesActions.SetWarehousesLoader({ isLoading: false }));
          })
        )
            
      ),
    )
  );
  
  AddWarehouseEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(warehousesActions.AddWarehouse),
      tap(() => {
        this.store.dispatch(warehousesActions.SetWarehousesSavingLoader({ isSaving: true }));
      }),
      switchMap((action) =>
        this.warehousesService.createWarehouse(action.warehouse).pipe(
          map((res: APIResponse<IWarehouse>) => warehousesActions.AddWarehouseSuccess({ warehouse: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.add.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(
            warehousesActions.AddWarehouseError()
          )),
          finalize(() => {
            this.store.dispatch(warehousesActions.SetWarehousesSavingLoader({ isSaving: false }));
          })
        )
      )
    )
  );
  UpdateWarehouseEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(warehousesActions.UpdateWarehouse),
      tap(() => {
        this.store.dispatch(warehousesActions.SetWarehousesSavingLoader({ isSaving: true }));
      }),
      switchMap((action) =>
        this.warehousesService.updateWarehouse(action.id, action.warehouse).pipe(
          map((res: APIResponse<IWarehouse>) => warehousesActions.UpdateWarehouseSuccess({ warehouse: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.update.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(
            warehousesActions.UpdateWarehouseError()
          )),
          finalize(() => {
            this.store.dispatch(warehousesActions.SetWarehousesSavingLoader({ isSaving: false }));
          })
        )
      ),
    )
  );
  DeleteWarehouseEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(warehousesActions.DeleteWarehouse),
      tap(() => {
        this.store.dispatch(warehousesActions.SetWarehousesSavingLoader({ isSaving: true }));
      }),
      switchMap((action) =>
        this.warehousesService.deleteWarehouse(action.id).pipe(
          map(() => warehousesActions.DeleteWarehouseSuccess({ id: action.id })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(
                `alerts.delete.success`
              ),
              'SUCCESS'
            );
          }),
          catchError(() => of(
            warehousesActions.DeleteWarehouseError()
          )),
          finalize(() => {
            this.store.dispatch(warehousesActions.SetWarehousesSavingLoader({ isSaving: false }));
          })
        )
      ),
    )
  );
}
